import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import { formatRupiah } from "../../utils/formatPrice";

export interface DoctorOption {
  id: number;
  doctorId: string;
  doctorName: string;
  doctorSpeciality: string;
  price_chat: number;
  availabilityStatus: string;
  photoUrl: string;
  rating: number;
  experience: number;
}

interface DoctorSwitchModalProps {
  isOpen: boolean;
  token: string;
  excludeRoleId?: string | number | null;
  title: string;
  description: string;
  stayLabel: string;
  confirmMessage: (doctor: DoctorOption) => React.ReactNode;
  showPrice?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: (doctor: DoctorOption) => void;
}

const FALLBACK_PHOTO = "https://m.linksehat.com/assets/img/users/dummy.jpg";

const DoctorSwitchModal: React.FC<DoctorSwitchModalProps> = ({
  isOpen,
  token,
  excludeRoleId,
  title,
  description,
  stayLabel,
  confirmMessage,
  showPrice = false,
  errorMessage = null,
  onClose,
  onConfirm,
}) => {
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDoctor, setConfirmDoctor] = useState<DoctorOption | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => setRefreshTick((t) => t + 1), 15000);
    return () => clearInterval(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setConfirmDoctor(null);
      setRefreshTick(0);
      return;
    }

    let active = true;
    if (refreshTick === 0) setLoading(true);

    axios
      .get("practitoner-roles", {
        params: { availabilityStatus: "ONLINE", limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const raw: any[] = res.data?.data ?? [];
        const mapped: DoctorOption[] = raw.map((d: any) => ({
          id: d.id,
          doctorId: d.doctorId,
          doctorName: d.doctorName,
          doctorSpeciality: d.doctorSpeciality || "Umum",
          price_chat: d.price_chat ?? 0,
          availabilityStatus: d.availabilityStatus,
          photoUrl: d.photoUrl || FALLBACK_PHOTO,
          rating: Number(d.rating) || 0,
          experience: Number(d.experience) || 0,
        }));

        setDoctors(
          mapped.filter(
            (d) =>
              d.availabilityStatus === "ONLINE" &&
              (excludeRoleId === null || excludeRoleId === undefined || String(d.id) !== String(excludeRoleId))
          )
        );
      })
      .catch((err) => {
        console.error("Gagal mengambil daftar dokter:", err);
        if (active) setDoctors([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isOpen, token, excludeRoleId, refreshTick]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-end z-[10001]">
        <div className="bg-white rounded-t-2xl w-full max-h-[70vh] flex flex-col">
          <div className="relative p-5 border-b border-gray-100">
            <button
              onClick={onClose}
              className="absolute right-5 top-5 text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>

            <div className="text-center px-6">
              <h2 className="text-base font-bold text-gray-800">{title}</h2>
              <p className="text-xs text-gray-500 mt-1">{description}</p>
            </div>
            {errorMessage && (
              <div className="mt-3 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg px-3 py-2 text-center">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="overflow-y-auto flex-1 px-4 py-2 space-y-2">
            {loading ? (
              <p className="text-sm text-gray-400 text-center py-6">Memuat daftar dokter...</p>
            ) : doctors.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                Tidak ada dokter lain yang tersedia saat ini
              </p>
            ) : (
              doctors.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-4 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50"
                >
                  <img
                    src={doc.photoUrl}
                    alt={doc.doctorName}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_PHOTO;
                    }}
                    className="w-11 h-11 rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm"
                  />
                  <div className="flex-1 flex flex-col items-center text-center px-2">
                    <p className="font-semibold text-sm text-gray-800">{doc.doctorName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{doc.doctorSpeciality}</p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <span className="text-yellow-400 text-xs">★</span>
                      <span className="text-xs text-gray-600">{doc.rating.toFixed(1)}</span>
                      <span className="text-gray-300 text-xs">•</span>
                      <span className="text-xs text-gray-500">{doc.experience} thn pengalaman</span>
                    </div>
                    {showPrice && (
                      <p className="text-xs font-semibold text-teal-600 mt-1">{formatRupiah(doc.price_chat)}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setConfirmDoctor(doc)}
                    className="flex-shrink-0 bg-teal-500 text-white text-xs font-semibold px-4 py-2 rounded-full"
                  >
                    Pilih
                  </button>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-full border border-teal-500 text-teal-500 text-sm font-medium"
            >
              {stayLabel}
            </button>
          </div>
        </div>
      </div>

      {confirmDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10002] px-5">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
            <div className="flex flex-col items-center px-5 pt-5 pb-4">
              <img
                src={confirmDoctor.photoUrl}
                alt={confirmDoctor.doctorName}
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_PHOTO;
                }}
                className="w-14 h-14 rounded-full object-cover border-2 border-teal-100"
              />
              <p className="font-bold text-gray-800 text-base mt-3 text-center">{confirmDoctor.doctorName}</p>
              <p className="text-sm text-gray-500 text-center">{confirmDoctor.doctorSpeciality}</p>
            </div>

            <div className="border-t border-gray-100 px-6 py-4">
              <h3 className="text-base font-bold text-gray-800 text-center">Pindah ke dokter ini?</h3>
              <p className="text-sm text-gray-500 text-center mt-3 leading-relaxed max-w-xs mx-auto">
                {confirmMessage(confirmDoctor)}
              </p>
            </div>

            <div className="flex border-t border-gray-100">
              <button
                onClick={() => setConfirmDoctor(null)}
                className="flex-1 py-3.5 text-gray-600 text-sm font-medium border-r border-gray-100"
              >
                Batal
              </button>
              <button
                onClick={() => {
                  const doctor = confirmDoctor;
                  setConfirmDoctor(null);
                  onConfirm(doctor);
                }}
                className="flex-1 py-3.5 text-teal-500 text-sm font-semibold"
              >
                Ya, Pindah
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DoctorSwitchModal;
